import {
  CalendarDayView,
  CalendarFilters,
  CalendarMonthView,
  CalendarView,
  CalendarWeekView,
  EventDialog,
  CalendarListView,
  CalendarYearView,
} from "@tellescope/calendar";
import { useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useParams, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { useShallowSidebarStore } from "../../store/sidebar-store";
import { useShallowCalendarStore } from "../../store";

export interface CalendarPageContentProps {
  view: CalendarView;
  startDate: Date;
  endDate: Date;
  referenceDate: Date;
  filters?: CalendarFilters;
  currentUserId?: string;
}

export const CalendarPageContent = ({
  view,
  startDate,
  endDate,
  referenceDate,
  filters,
  currentUserId,
}: CalendarPageContentProps) => {
  const { t } = useTranslation("calendar");
  const [searchParams, setSearchParams] = useSearchParams();
  const { openSidebar } = useShallowSidebarStore("openSidebar");
  const { selectedTimezone, showAvailabilityOverlay } = useShallowCalendarStore(
    "selectedTimezone",
    "showAvailabilityOverlay"
  );
  const selectedEventId = searchParams.get("eventId");
  const params = useParams();
  const viewId = params.viewId;

  const closeEnduserDialog = useCallback(() => {
    setSearchParams(
      (prev) => {
        prev.delete("eventId");
        return prev;
      },
      { replace: true }
    );
  }, [setSearchParams]);

  return (
    <>
      {view === "month" ? (
        <CalendarMonthView
          startDate={startDate}
          endDate={endDate}
          referenceDate={referenceDate}
          className="flex-grow flex flex-col"
          timezone={selectedTimezone}
          filters={filters}
          currentUserId={currentUserId}
        />
      ) : view === "week" ? (
        <CalendarWeekView
          startDate={startDate}
          endDate={endDate}
          referenceDate={referenceDate}
          className="flex-grow flex flex-col"
          timezone={selectedTimezone}
          getOnDayClick={(date) =>
            `/calendar/view/${viewId}/day/${date.getFullYear()}/${date.getMonth()}/${date.getDate()}`
          }
          filters={filters}
          currentUserId={currentUserId}
          showAvailability={showAvailabilityOverlay}
        />
      ) : view === "day" ? (
        <CalendarDayView
          startDate={startDate}
          endDate={endDate}
          referenceDate={referenceDate}
          className="flex-grow flex flex-col"
          timezone={selectedTimezone}
          getOnDayClick={(date) =>
            `/calendar/view/${viewId}/day/${date.getFullYear()}/${date.getMonth()}/${date.getDate()}`
          }
          filters={filters}
          currentUserId={currentUserId}
          showAvailability={showAvailabilityOverlay}
        />
      ) : view === "list" ? (
        <CalendarListView
          startDate={startDate}
          endDate={endDate}
          referenceDate={referenceDate}
          className="flex-grow flex flex-col"
          timezone={selectedTimezone}
          getOnDayClick={(date) =>
            `/calendar/view/${viewId}/day/${date.getFullYear()}/${date.getMonth()}/${date.getDate()}`
          }
          getOnEventClick={(eventId) => ({ to: `.?eventId=${eventId}`, replace: true })}
          filters={filters}
          currentUserId={currentUserId}
        />
      ) : view === "year" ? (
        <CalendarYearView
          startDate={referenceDate}
          endDate={endDate}
          getOnDateClick={(date) => ({
            to: `/calendar/view/${viewId}/day/${date.getFullYear()}/${date.getMonth()}/${date.getDate()}`,
          })}
          getOnHeaderClick={(month) => ({
            to: `/calendar/view/${viewId}/month/${month.getFullYear()}/${month.getMonth()}`,
          })}
          filters={filters}
          currentUserId={currentUserId}
        />
      ) : null}
      <EventDialog
        eventId={selectedEventId ?? ""}
        open={!!selectedEventId}
        onOpenChange={(open) => {
          if (!open) {
            closeEnduserDialog();
          }
        }}
        onCopyId={() => {
          toast.success(t("Event ID copied!"));
        }}
        onEnduserClick={(enduserId) => {
          openSidebar("ContactDetails", { enduserId, selectedSection: "Events" });
          closeEnduserDialog();
        }}
        onSuccess={() => {
          toast.success(t("Event updated"));
        }}
        timezone={selectedTimezone}
      />
    </>
  );
};
